import { Recurrence, SelfServiceSchedulePane, StaticInitializers } from '@tableau/self-service-schedules';
import { SelfServiceSchedulePaneType } from '@tableau/self-service-schedules/lib/src/components/SelfServiceSchedulePane';
import { SiteUrls } from '@tableau/shared-urls';
import React, { useEffect, useState } from 'react';

import { Set } from '~commonInfra/utils/Set';
import { Messages } from '~src/codegen/Localize';
import { ActionHelpers, standardActionErrorMsg, standardActionSuccessMsg } from '~src/commonInfra/contentActions/ActionHelpers';
import { IErrorDetail } from '~src/commonInfra/contentActions/ActionMessages';
import { IErrorItem } from '~src/commonInfra/contentActions/MultipleItemResult';
import { openReactActionDialog,ReactActionDialogBodyProps } from '~src/commonInfra/dialogs/ReactActionDialog';
import { TabDialogWidth } from '~src/commonInfra/dialogs/style/TabDialogWidth';
import { CommonErrorCode } from '~src/commonInfra/utils/ErrorCodes';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { LinkedTasks } from '~src/linkedTasks/resources/LinkedTasks';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';

import { RunFlowTaskInfo } from '../models/RunFlowTaskInfo';
import { RunFlowTasks } from '../resources/RunFlowTasks';
import { convertRecurrenceToSelfServiceScheduleViewModel } from '../utils/FlowSelfServiceSchedulesUtils';
import { FlowTaskSelfServiceSchedulePickerBanner } from './FlowTaskSelfServiceSchedulePickerBanner';

export type SetRunFlowTaskFrequencyDialogProps = {
  runFlowTasks: RunFlowTaskInfo[];
}

export type SetRunFlowTaskFrequencyDialogState = {
  recurrence: Recurrence;
}

export type PropsForSetRunFlowTaskFrequencyDialogBody = ReactActionDialogBodyProps<SetRunFlowTaskFrequencyDialogProps, SetRunFlowTaskFrequencyDialogState>;

export function openSetRunFlowTaskFrequencyDialog(props: SetRunFlowTaskFrequencyDialogProps) {
  const linkedTasks = props.runFlowTasks.filter(task => task.taskType === 'LinkedTask');
  const linkedTaskIds = linkedTasks.map(task => task.linkedTaskId);
  const flowTasks = props.runFlowTasks.filter(task => task.taskType === 'FlowTask');
  const flowTaskIds = flowTasks.map(task => task.id);
  const flowTaskNotificationTranslationFns = {
    success: standardActionSuccessMsg(Messages.changeScheduleRunFlowTasksSuccess),
    error: standardActionErrorMsg(Messages.changeScheduleRunFlowTasksError),
  };
  const linkedTaskNotificationTranslationFns = {
    success: standardActionSuccessMsg(Messages.changeScheduleRunLinkedTasksSuccess),
    error: standardActionErrorMsg(Messages.changeScheduleRunLinkedTasksError),
  };
  const modalInstance = openReactActionDialog<SetRunFlowTaskFrequencyDialogProps, SetRunFlowTaskFrequencyDialogState, IResult>({
    title: Messages.changeSubscriptionsFrequency(),
    bodyComponent: SetRunFlowTaskFrequencyDialogContent,
    initialBodyProps: {
      ...props,
    },
    initialDialogState: { recurrence: StaticInitializers.defaultRecurrence()},
    actionName: Messages.changeSubscriptionsFrequency(),
    action: (props: SetRunFlowTaskFrequencyDialogProps, state: SetRunFlowTaskFrequencyDialogState) =>
    Promise.all([
      flowTaskIds.length > 0 && RunFlowTasks.setSchedule(flowTaskIds,'', convertRecurrenceToSelfServiceScheduleViewModel(state.recurrence)),
      linkedTaskIds.length > 0 && LinkedTasks.updateLinkedTasksSchedule(linkedTaskIds,'', convertRecurrenceToSelfServiceScheduleViewModel(state.recurrence))
    ]).then(results => {
      ActionHelpers.displayStandardToastsForResult(
        Set.fromIdItemArray(flowTasks),
        results[0],
        flowTaskNotificationTranslationFns,
        (errorItem: IErrorItem<RunFlowTaskInfo>) => errorDetailForFlowTask(errorItem)
      );
      ActionHelpers.displayStandardToastsForResult(
        Set.fromIdItemArray(linkedTasks),
        results[1],
        linkedTaskNotificationTranslationFns,
        (errorItem: IErrorItem<RunFlowTaskInfo>) => errorDetailForFlowTask(errorItem)
      );
      return Promise.resolve(results[0]);
  }),
    dialogWidth: TabDialogWidth.width900px,
    dismissDialogOnActionFailed: false,
    showCloseButton: true,
    cancelButtonText: Messages.close(),
  });
  return modalInstance.result;
}

function errorDetailForFlowTask(errorItem: IErrorItem<RunFlowTaskInfo>): IErrorDetail {
  const itemName = errorItem.item ? errorItem.item.schedule.name : '';
  let errorMessage: string;

  switch (errorItem.error.code) {
    case CommonErrorCode.CallingUserInsufficientPermissions:
      errorMessage = Messages.runFlowTasksInsufficientPermissions();
      break;
    default:
      errorMessage = Messages.errorUnknown();
  }

  return { itemName, errorMessage };
}

export function SetRunFlowTaskFrequencyDialogContent(props: PropsForSetRunFlowTaskFrequencyDialogBody): JSX.Element {
  const [timeZoneDetails, setTimeZoneDetails] = useState(null);

  useEffect(() => {
    const server = getSessionManagementServiceInstance();
    const userAccountSettingsLink = new SiteUrls({
      siteUrlName: server.sessionSiteUrlName(),
      queryString: window.location.search,
    }).userSettingsUrl(
      server.sessionUserInfo().username,
      server.sessionUserInfo().domainName
    );
    setTimeZoneDetails({
      timeZonesEnabled: false,
      timeZoneDisplayName: server.userTimeZone().displayName.replace('_', ' '),
      linkToUserSettings: userAccountSettingsLink,
    });
  },[]);

  return (
    <>
      <FlowTaskSelfServiceSchedulePickerBanner timeZoneDetails={timeZoneDetails} recurrence={props.dialogState.recurrence} solidBackground={false}/>
      <SelfServiceSchedulePane
        recurrenceModel={props.dialogState.recurrence}
        recurrenceModelChangedCallback={(recurrence) => {
          props.updateDialogState({
            recurrence: recurrence,
          });
        }
        }
        timeZoneDetails={timeZoneDetails}
        type={SelfServiceSchedulePaneType.FlowRunTask}
        uncollapsible={true}
      />
    </>
  );
}
